import { useCallback, useEffect, useState } from 'react';
import { studioApi, studioMock, requestKey, idempotencyKey } from '../../../../../data/studioApi';
import { STUDIO_AUTOMATIONS_CONNECT } from '../../../../../enums/routes';

// Which channel, and where the official connection of the active profile's account stands
// (contract 2.14.0, 01). Connecting leaves for the Meta dialog in the whole tab and comes back with
// `?connection=scn-…&result=…&code=…`; the mock asks here what the dialog would have answered.
export default function useConnect(s) {
  const profileId = s.profile.id;
  const [platforms, setPlatforms] = useState(null);
  const [connection, setConnection] = useState(null);
  const [status, setStatus] = useState('loading');
  // The channel the creator picked; a connection in any state implies Instagram.
  const [channel, setChannel] = useState(null);
  const [busy, setBusy] = useState(null);
  // The authorization the mock's Meta stand-in is answering.
  const [authorizing, setAuthorizing] = useState(null);
  const [outcome, setOutcome] = useState(null);

  const settle = useCallback(async (id, result, code) => {
    const next = await studioApi.connection(id);
    setConnection(next);
    setOutcome(result ? { result, code: code || next.last_error?.code || null } : null);
    s.automations.refresh();
    return next;
  }, [s.automations]);

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const params = new URLSearchParams(s.location.search);
      const returned = params.get('connection');
      const [catalog, list] = await Promise.all([studioApi.platforms({ profile_id: profileId }), studioApi.connections({ profile_id: profileId })]);
      setPlatforms(catalog.data);
      const current = list.data.find(item => item.platform === 'instagram') || null;
      setConnection(current);
      // The callback's return: the connection is read again and the query leaves the URL.
      if (returned) {
        await settle(returned, params.get('result'), params.get('code'));
        s.navigate(STUDIO_AUTOMATIONS_CONNECT, { replace: true });
      }
      setStatus('ready');
    } catch { setStatus('error'); }
  }, [profileId, s, settle]);
  // Only when the profile changes: `load` reads the URL once and then clears it.
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [profileId]);

  // POST /connections, then the whole tab goes to the dialog. `force_reauth` asks Meta for the login
  // again: another account, or the permissions left unchecked.
  const connect = useCallback(async ({ reauth = false } = {}) => {
    setBusy('connect');
    setOutcome(null);
    try {
      const body = { profile_id: profileId, platform: 'instagram', ...(reauth ? { force_reauth: true } : {}) };
      const next = await studioApi.createConnection(body, requestKey('connection', { ...body, at: Date.now() }));
      setConnection(next);
      if (studioMock) { setAuthorizing(next.id); return; }
      window.location.assign(next.authorization.url);
    } catch (failure) {
      s.act(() => { throw failure; });
    } finally { setBusy(null); }
  }, [profileId, s]);

  // The mock's Meta stand-in answered; the real callback redirects with the same three values.
  const answerMeta = useCallback(async answer => {
    setBusy(answer);
    try {
      const { connection: next, result, code } = await studioMock.completeConnection(authorizing, answer);
      setConnection(next);
      setOutcome({ result, code });
      setAuthorizing(null);
      s.automations.refresh();
    } catch (failure) {
      s.act(() => { throw failure; });
    } finally { setBusy(null); }
  }, [authorizing, s]);

  const disconnect = useCallback(async () => {
    setBusy('disconnect');
    setOutcome(null);
    try {
      setConnection(await studioApi.disconnect(connection.id, idempotencyKey('disconnect')));
      s.automations.refresh();
    } catch (failure) {
      s.act(() => { throw failure; });
    } finally { setBusy(null); }
  }, [connection, s]);

  const instagram = connection && connection.status !== 'disconnected' ? connection : null;
  // What the right side shows: the channels, this channel's connection, or a refused account.
  const view = connection?.conflict ? 'conflict'
    : instagram?.status === 'connected' ? 'connected'
    : instagram?.status === 'reauthorization_required' ? 'reauthorize'
    : channel === 'instagram' || outcome ? 'instagram'
    : 'channels';
  return { status, platforms, connection, view, busy, outcome, authorizing, reload: load, connect, disconnect, answerMeta, chooseChannel: setChannel, backToChannels: () => { setOutcome(null); setChannel(null); }, cancelMeta: () => answerMeta('cancelled') };
}
